import { cn } from '@/lib/utils';
import { SettlementScheduleRowVm } from '@/features/dashboard/bookingChanges/types/ChangePackageFlowViewModel.ts';
import MoneyAmountList from '@/features/dashboard/bookingChanges/components/primitives/MoneyAmountList.tsx';
import { getInstallmentStatusLabel } from '@/features/dashboard/bookingChanges/types/BillViewModel.utils.ts';
import { PlannedTransactionPaymentStatus } from '@/features/public/bookingSummary/types/PlannedTransactionPaymentStatus.ts';

/** Druga linia kwoty: stara wartość, „nowa” dla dopisanej pozycji, „bez zmian”. */
const SecondaryAmount = ({ row }: { row: SettlementScheduleRowVm }) => {
  if (row.isNew) {
    return 'nowa';
  }
  if (row.previousAmount) {
    return <MoneyAmountList values={row.previousAmount} />;
  }
  return 'bez zmian';
};

interface SettlementScheduleRowProps
  extends React.HTMLAttributes<HTMLDivElement> {
  row: SettlementScheduleRowVm;
}

/**
 * Rata po zmianie: nazwa z terminem i kwota z drugą linią. Rata ruszona
 * dopłatą stoi na czarno nad przekreśloną starą kwotą, nietknięta zostaje
 * wyszarzona.
 *
 * Powód zamrożenia jedzie przy terminie, a nie zamiast „bez zmian”: dwóch
 * z pięciu powodów (otwarta płatność online, kwota niedodatnia) po samej racie
 * nie widać, a rata nietknięta bez wytłumaczenia wygląda na błąd systemu.
 */
const SettlementScheduleRow = ({
  row,
  className,
  ...props
}: SettlementScheduleRowProps) => (
  <div className={cn('flex items-start gap-3 py-2.5', className)} {...props}>
    <div className="min-w-0">
      <div
        className={cn(
          'text-sm font-semibold text-ink',
          row.cancelled && 'line-through',
        )}
      >
        {row.name}
      </div>
      <div className="mt-0.5 text-[13px] text-neutral-500">
        {row.dueDateLabel}
        {/* Rata zredukowana do zera zniknie z należności jako anulowana, a nie
            zostanie ratą na 0 zł — tabela rat nazywa to wprost, więc podgląd też
            musi, bo samo przekreślenie nazwy łatwo wziąć za styl wiersza. */}
        {row.cancelled && (
          <span className="text-neutral-400">
            {` · ${getInstallmentStatusLabel(PlannedTransactionPaymentStatus.CANCELLED).toLowerCase()}`}
          </span>
        )}
        {row.frozenLabel && (
          <span className="text-neutral-400" title={row.frozenHint}>
            {` · ${row.frozenLabel}`}
          </span>
        )}
      </div>
    </div>

    <div className="ml-auto text-right">
      <div
        className={cn(
          'text-[15px] font-bold tabular-nums',
          row.unchanged ? 'text-neutral-500' : 'text-ink',
        )}
      >
        <MoneyAmountList values={row.amount} />
      </div>
      <div
        className={cn(
          'mt-0.5 text-[13px] tabular-nums text-neutral-400',
          row.previousAmount && !row.isNew && 'line-through',
        )}
      >
        <SecondaryAmount row={row} />
      </div>
    </div>
  </div>
);

export default SettlementScheduleRow;
